import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { categories, getMediaByType } from "@/lib/media";

const FORM_ADDR = "https://readdy.ai/api/form/db6c46jghk82orf0ck30";

type Status = "idle" | "submitting" | "success" | "error";

export default function About() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  useDocumentMeta({
    title: "关于本站 - 91视频网站影视资源平台",
    description:
      "了解91视频网站的定位、内容范围与联系方式。我们专注于聚合热门电影、电视剧与综艺资源，帮助影视爱好者便捷发现想看的内容。",
    keywords: "关于91视频网站,影视平台介绍,联系我们,影视资源平台",
    canonicalPath: "/about",
  });

  const totalCount = categories.reduce(
    (sum, category) => sum + getMediaByType(category.key).length,
    0,
  );

  const highlights = [
    { icon: "ri-database-2-line", title: "资源丰富", desc: "覆盖电影、电视剧、综艺三大品类，内容持续更新。" },
    { icon: "ri-filter-3-line", title: "筛选便捷", desc: "支持类型、地区、年份多维筛选，快速锁定目标内容。" },
    { icon: "ri-search-line", title: "搜索精准", desc: "关键词匹配片名、简介与演员，找片一步到位。" },
    { icon: "ri-smartphone-line", title: "移动友好", desc: "全站适配手机浏览，触控操作顺畅不费劲。" },
  ];

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }
    formData.delete("website_alt");

    setStatus("submitting");
    setFormError("");

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      params.append(key, String(value));
    });

    try {
      const response = await fetch(FORM_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg && !isSpam
            ? serverMsg
            : "提交失败，请稍后重试，或通过邮箱与我们联系。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请检查网络后重试。");
    }
  };

  return (
    <div className="w-full">
      <section className="w-full bg-background-100 px-4 py-12 md:px-8 md:py-16">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-600">
            <i className="ri-information-line" aria-hidden="true"></i>
            关于我们
          </span>
          <h1 className="mt-4 font-heading text-2xl font-extrabold text-foreground-950 md:text-4xl">
            关于 91视频网站
          </h1>
          <p className="mt-4 text-sm leading-loose text-foreground-700 md:text-base">
            91视频网站是一个面向广大影视爱好者和追剧用户的影视资源聚合展示平台。我们聚焦热门影视资源的发现与浏览，
            通过清晰的内容分类、多维度的筛选与便捷的搜索，帮助你在海量内容中快速找到想看的那一部，
            让“发现好片”这件事变得简单又省心。
          </p>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4">
          <div className="rounded-lg border border-background-200 bg-background-50 p-4">
            <p className="font-heading text-2xl font-extrabold text-primary-600">{totalCount}+</p>
            <p className="mt-1 text-xs text-foreground-500">收录影视作品</p>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-50 p-4">
            <p className="font-heading text-2xl font-extrabold text-primary-600">3</p>
            <p className="mt-1 text-xs text-foreground-500">主要分类板块</p>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-50 p-4">
            <p className="font-heading text-2xl font-extrabold text-primary-600">10+</p>
            <p className="mt-1 text-xs text-foreground-500">细分影视题材</p>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-50 p-4">
            <p className="font-heading text-2xl font-extrabold text-primary-600">7×24</p>
            <p className="mt-1 text-xs text-foreground-500">内容持续更新</p>
          </div>
        </div>
      </section>

      <section className="w-full px-4 py-10 md:px-8 md:py-14">
        <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">我们的特色</h2>
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {highlights.map((item) => (
            <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                <i className={`${item.icon} text-xl`} aria-hidden="true"></i>
              </span>
              <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
              <p className="mt-1.5 text-xs leading-relaxed text-foreground-500 md:text-sm">{item.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          {categories.map((category) => (
            <Link
              key={category.key}
              to={category.path}
              className="flex items-center gap-2 rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 cursor-pointer whitespace-nowrap"
            >
              <i className={category.icon} aria-hidden="true"></i>
              浏览{category.name}
            </Link>
          ))}
        </div>
      </section>

      <section className="w-full bg-background-100 px-4 py-10 md:px-8 md:py-14">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[320px_1fr]">
          <div>
            <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">联系我们</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              如果你对网站有任何建议、内容纠错或合作意向，欢迎通过以下方式与我们取得联系。
            </p>
            <ul className="mt-5 space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                  <i className="ri-mail-line" aria-hidden="true"></i>
                </span>
                <span>
                  <span className="block text-foreground-400">邮箱</span>
                  <span className="text-foreground-900">support@91video.example.com</span>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                  <i className="ri-customer-service-2-line" aria-hidden="true"></i>
                </span>
                <span>
                  <span className="block text-foreground-400">服务时间</span>
                  <span className="text-foreground-900">每日 09:00 - 21:00</span>
                </span>
              </li>
            </ul>
          </div>

          <div>
            <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">意见反馈</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              填写下面的表单给我们留言，我们会认真阅读每一条反馈。
            </p>

            {status === "success" ? (
              <div className="mt-5 flex items-start gap-3 rounded-lg border border-primary-200 bg-primary-50 p-4">
                <i className="ri-checkbox-circle-fill text-xl text-primary-500" aria-hidden="true"></i>
                <div>
                  <p className="text-sm font-semibold text-foreground-950">提交成功，感谢你的反馈！</p>
                  <p className="mt-1 text-xs text-foreground-600">我们会尽快查看并处理你的留言。</p>
                  <button
                    type="button"
                    onClick={() => setStatus("idle")}
                    className="mt-3 text-xs font-medium text-primary-600 underline cursor-pointer"
                  >
                    再提交一条
                  </button>
                </div>
              </div>
            ) : (
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="mt-5 space-y-4 rounded-lg border border-background-200 bg-background-50 p-5"
              >
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="about-name" className="mb-1.5 block text-sm text-foreground-700">
                      称呼
                    </label>
                    <input
                      id="about-name"
                      type="text"
                      name="name"
                      required
                      placeholder="请输入你的称呼"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-email" className="mb-1.5 block text-sm text-foreground-700">
                      邮箱
                    </label>
                    <input
                      id="about-email"
                      type="email"
                      name="email"
                      required
                      placeholder="用于接收我们的回复"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="about-subject" className="mb-1.5 block text-sm text-foreground-700">
                    反馈类型
                  </label>
                  <select
                    id="about-subject"
                    name="subject"
                    defaultValue="内容纠错"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400 cursor-pointer"
                  >
                    <option value="内容纠错">内容纠错</option>
                    <option value="功能建议">功能建议</option>
                    <option value="影视推荐">影视推荐</option>
                    <option value="合作洽谈">合作洽谈</option>
                    <option value="其他反馈">其他反馈</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="about-message" className="mb-1.5 block text-sm text-foreground-700">
                    留言内容
                  </label>
                  <textarea
                    id="about-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="请写下你的想法或建议（最多 500 字）"
                    className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400"
                  ></textarea>
                </div>

                <input
                  type="text"
                  name="website_alt"
                  className="form-honeypot-field"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                {status === "error" ? (
                  <p className="flex items-center gap-2 rounded-md bg-accent-50 px-3 py-2 text-sm text-accent-800">
                    <i className="ri-error-warning-line" aria-hidden="true"></i>
                    {formError}
                  </p>
                ) : null}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="flex items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-send-plane-line" aria-hidden="true"></i>
                  {status === "submitting" ? "提交中…" : "提交反馈"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <section className="w-full px-4 py-10 md:px-8 md:py-14">
        <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">版权声明与使用须知</h2>
        <div className="mt-4 space-y-4 text-sm leading-loose text-foreground-700 md:text-base">
          <p>
            本站为影视内容展示与技术演示站点，站内所有影视作品名称、简介、评分及图片均为虚构示例，仅用于页面功能与界面效果演示，
            不代表任何真实影视作品。
          </p>
          <p>
            本站不提供任何在线播放、下载或资源存储服务，也不包含登录、注册与支付功能。所有内容仅供用户了解与浏览，
            请通过正规渠道观看正版影视作品，支持影视行业发展。
          </p>
          <p>
            如本站展示的示例内容无意中与现有作品雷同，纯属巧合。若权利人认为内容存在异议，可通过上方邮箱与我们联系，
            我们将及时处理。
          </p>
        </div>
      </section>
    </div>
  );
}